import type { ApiClient } from '@klareospace/pharos-lib-front-utils';
import { mapPaginatedResponse, toQueryParams } from '@klareospace/pharos-lib-front-utils';
import type {
  Location,
  PaginatedResponse,
  PaginationParams,
  PharosApiResponse,
  PharosPaginatedResponse,
  PhoneNumber,
  Site,
  SiteType,
  SiteWithSpaceName,
  SortParams,
  UpdateSite,
} from '@klareospace/pharos-lib-types';

import type { SiteSortableColumn } from '../types/site.types';

// ─── Params ───────────────────────────────────────────────────────────────────

export interface SearchSitesParams extends PaginationParams {
  excludeIds?: string[];
  ids?: string[];
  name?: string;
  sort?: SortParams<SiteSortableColumn>;
  spaceIds?: string[];
  types?: SiteType[];
}

export interface CreateSiteParams {
  code: string;
  color: string;
  customerSupportPhone?: PhoneNumber;
  description?: string;
  location?: Location;
  name: string;
  phone?: PhoneNumber;
  spaceId: string;
  type: SiteType;
}

// Identical to `UpdateSite` — re-exported under the app's naming convention so call sites are unaffected.
export type UpdateSiteParams = UpdateSite;

// ─── API Factory ──────────────────────────────────────────────────────────────

export type SiteApi = {
  checkCodeExists: (code: string) => Promise<boolean>;
  createSite: (params: CreateSiteParams) => Promise<Site>;
  getSite: (id: string) => Promise<Site>;
  searchSites: (params: SearchSitesParams) => Promise<PaginatedResponse<SiteWithSpaceName>>;
  updateSite: (id: string, params: UpdateSiteParams) => Promise<Site>;
};

export function createSiteApi(client: ApiClient): SiteApi {
  return {
    checkCodeExists: async (code: string): Promise<boolean> => {
      // Sent as `value`, not `code` — `code` is reserved by Azure Functions (access keys)
      // and never reaches the backend.
      const response = await client.execute<PharosApiResponse<{ exists: boolean }>>(() =>
        client.instance.get('/sites/check-code', { params: { value: code } }),
      );
      return response.data?.exists ?? false;
    },

    createSite: async (params: CreateSiteParams): Promise<Site> => {
      const response = await client.execute<PharosApiResponse<Site>>(() => client.instance.post('/sites', params));
      if (!response.data) {
        throw new Error('createSite: unexpected empty response');
      }
      return response.data;
    },

    getSite: async (id: string): Promise<Site> => {
      const response = await client.execute<PharosApiResponse<Site>>(() => client.instance.get(`/sites/${id}`));
      if (!response.data) {
        throw new Error('getSite: unexpected empty response');
      }
      return response.data;
    },

    searchSites: async (params: SearchSitesParams): Promise<PaginatedResponse<SiteWithSpaceName>> => {
      const body = toSearchBody(params);
      const query = toQueryParams(params);
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<SiteWithSpaceName>>>(() =>
        client.instance.post('/sites/search', body, { params: query }),
      );
      if (!response.data) {
        throw new Error('searchSites: unexpected empty response');
      }
      return mapPaginatedResponse(response.data);
    },

    updateSite: async (id: string, params: UpdateSiteParams): Promise<Site> => {
      const response = await client.execute<PharosApiResponse<Site>>(() =>
        client.instance.patch(`/sites/${id}`, params),
      );
      if (!response.data) {
        throw new Error('updateSite: unexpected empty response');
      }
      return response.data;
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toSearchBody(params: SearchSitesParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const trimmedName = params.name?.trim();

  const conditionalFields: [string, unknown][] = [
    ['name', trimmedName],
    ['ids', params.ids],
    ['spaceIds', params.spaceIds],
    ['types', params.types],
    ['excludeIds', params.excludeIds],
  ];

  for (const [key, value] of conditionalFields) {
    if (Array.isArray(value) ? value.length > 0 : value) {
      body[key] = value;
    }
  }

  return body;
}
